import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { Router, RouterModule } from '@angular/router';
import currency from 'currency.js';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

import { IDealList } from '../../modules/advance-application/models/deal.interface';
import { DealStatusEnum } from '../../modules/advance-application/models/deal-status.enum';
import { DealService } from '../../modules/advance-application/services/deal.service';
import { PromisedPaymentService } from '../../modules/advance-application/services/promised-payment.service';
import { ProductEnum } from '../../modules/authentication/models/product.enum';
import { IUserReferralReward } from '../../modules/authentication/models/user-referral-reward.interface';
import { User } from '../../modules/authentication/models/v1-api.interface';
import { AuthenticationService } from '../../modules/authentication/services/authentication.service';
import { CurrencyJsPipe } from '../../modules/pipes/currency-js.pipe';
import { LocalisePipe } from '../../modules/pipes/localise.pipe';
import { ReferFriendComponent } from '../../modules/shared/components/refer-friend/refer-friend.component';
import {
  optionSnackbar,
  SnackbarCustomComponent,
} from '../../modules/shared/components/snackbar-custom/snackbar-custom.component';
import { List } from '../../modules/shared/models/list.model';
import { AccountService } from '../../modules/shared/services/account.service';
import { SharedService } from '../../modules/shared/services/shared.service';
import { TransactionService } from '../../modules/shared/services/transaction.service';
import { RegistrationService } from '../../modules/signup/services/registration.service';
import { NavbarService } from '../../services/navbar.service';
import { ThemesService } from '../../services/themes.service';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    MatButtonModule,
    MatDividerModule,
    MatExpansionModule,
    MatIconModule,
    MatMenuModule,
    MatDialogModule,
    MatSnackBarModule,
    LocalisePipe,
    CurrencyJsPipe,
  ],
})
export class SidebarComponent implements OnInit, OnDestroy {
  @ViewChild('promoCodeTmp') promoCode: TemplateRef<any>;

  public showMenu = false;
  public isAuthenticated = false;
  public user: User | null = null;
  public availableBalance = currency(0);
  public balance = currency(0);
  public hasTaxDocuments?: boolean;
  public readonly productEnum = ProductEnum;
  private unsubscribe$ = new Subject<boolean>();
  public isReward = false;
  public deal: List<IDealList>;
  public bonusAmount = currency(100);
  public amountReward = currency(0);
  public userReferralRewardArr: IUserReferralReward[] = [];
  private loading = false;

  constructor(
    private accountService: AccountService,
    private transactionService: TransactionService,
    private promisedPaymentService: PromisedPaymentService,
    private dealService: DealService,
    private matDialog: MatDialog,
    private snackBar: MatSnackBar,
    private sharedService: SharedService,
    private toastrService: ToastrService,
    public navbarService: NavbarService,
    public themesService: ThemesService,
    public authenticationService: AuthenticationService,
    public registrationService: RegistrationService,
    public router: Router,
  ) {
    // redirect the user if they're already authenticated.
    this.authenticationService.isAuth.subscribe((isAuthenticated) => {
      this.isAuthenticated = isAuthenticated;

      if (this.isAuthenticated) {
        this.navbarService.closeMenuIfMobile();
        this.loadBalance();
      }
    });
  }

  ngOnInit(): void {
    this.authenticationService.profile
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(async (user) => {
        this.user = user;

        if (typeof this.hasTaxDocuments === 'boolean' || !user.id) {
          return;
        }

        const list = await this.promisedPaymentService
          .getStatementsByYears()
          .toPromise();
        this.hasTaxDocuments = !!list?.length;

        if (this.isAuthenticated && user?.id) {
          this.checkReward();

          try {
            const resp = await firstValueFrom(
              this.registrationService.getReferralBonus(),
            );
            this.bonusAmount = currency(resp);
          } catch (e) {
            // Handle referral bonus API errors gracefully
            // Keep default value of $100 if API call fails
            console.warn('Failed to load referral bonus:', e);
          }
        }
      });

    this.transactionService.transactionUpdated
      .pipe(
        takeUntil(this.unsubscribe$),
        filter(() => this.isAuthenticated),
      )
      .subscribe(() => this.loadBalance());

    this.navbarService.closeTransferMoney
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.checkReward();
      });
  }

  ngOnDestroy(): void {}

  private async loadBalance(): Promise<void> {
    const b = await firstValueFrom(this.accountService.getBalance());
    this.availableBalance = b.available;
    this.balance = b.balance;
  }

  toggleMenu(): void {
    this.showMenu = !this.showMenu;
  }

  logout(): void {
    this.authenticationService.logout().subscribe();
  }

  /**
   * Open /profile page
   */
  openProfile(): void {
    this.router.navigate(['/profile']);
  }

  openHomePage(): void {
    if (this.isAuthenticated) {
      this.router.navigate(['/dashboard']);
    } else {
      this.router.navigate(['/login']);
    }
  }

  public async openApplyNow(): Promise<void> {
    await this.dealService.openApplyNow();
  }

  public async openReferFriend(): Promise<void> {
    const dialogRef = this.matDialog.open(ReferFriendComponent, {
      minHeight: '330px',
      panelClass: this.themesService.getDarkThemeClass(),
      backdropClass: this.themesService.getDarkThemeClass(),
    });
  }

  openHelp(): void {
    window.open('https://help.payfully.co/knowledge', '_blank');
  }

  public async openPromoCodeView(): Promise<void> {
    this.deal = await firstValueFrom(
      this.dealService.getList({
        limit: 1,
        offset: 0,
        sortBy: 'createdAt',
        sortDirection: 'desc',
        status: [DealStatusEnum.Approved],
      }),
    );

    this.matDialog
      .open(this.promoCode, {
        width: '723px',
        autoFocus: false,
        panelClass: [this.themesService.getDarkThemeClass(), 'promo-code'],
        backdropClass: this.themesService.getDarkThemeClass(),
      })
      .afterClosed()
      .subscribe(async () => {
        this.checkReward();
      });
  }
  public copyCode(): void {
    if (!this.user) {
      return;
    }
    navigator.clipboard.writeText(this.user?.referralInviteCode);

    this.snackBar.openFromComponent(SnackbarCustomComponent, {
      ...optionSnackbar,
      panelClass: ['snackbar-custom', 'snackbar-color-green'],
      announcementMessage:
        'Your referral code has been copied to your clipboard!',
    });
  }

  public async openTransferMoney(): Promise<void> {
    if (!this.router.url.startsWith('/dashboard')) {
      await this.router.navigate(['/dashboard']);
    }
    this.navbarService.openTransferMoney$.next();
  }

  public async claimReward(): Promise<void> {
    if (this.loading) {
      return;
    }
    try {
      this.loading = true;
      await this.authenticationService.GetReferralReward().toPromise();
      this.isReward = false;
      this.checkReward();
      this.navbarService.emitRewardClaimed();
    } catch (e) {
      this.toastrService.error(this.sharedService.getErrorText(e, true));
    } finally {
      this.loading = false;
    }
  }

  private async checkReward(): Promise<void> {
    try {
      this.userReferralRewardArr = await firstValueFrom(
        this.authenticationService.referralReward(),
      );

      if (this.userReferralRewardArr?.length) {
        this.amountReward = this.userReferralRewardArr.reduce(
          (acc, item) => acc.add(currency(item.amount || 0)),
          currency(0),
        );

        this.isReward = true;
      }
    } catch (e) {
      this.isReward = false;
    }
  }
}
